import type { AppointmentPrintLocationResult } from "@ascleo/frontdesk-contracts/appointment-printing/model";
import type { AppointmentPrintOutcome } from "@ascleo/frontdesk-contracts/appointment-printing/native";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePlatform } from "../../../../platform/context";
import { reportOperationalError } from "../../../../lib/operational-error-telemetry";
import { docCirrusRpcClient } from "../../../../lib/rpc/doc-cirrus.client";
import {
  buildAppointmentPrintSnapshot,
  type AppointmentSlipLabels,
  type ConfirmedAppointmentPrintBooking,
} from "./appointment-slip";

const PRINT_DEADLINE_MS = 300_000;

interface ActiveAppointmentPrintAttempt {
  readonly id: string;
  readonly deadlineAt: number;
  readonly deadlineTimer: ReturnType<typeof setTimeout>;
}

/** Patient-visible state of one explicit appointment print attempt. @since 1.2.0 @example `const state: AppointmentPrintControllerState = { kind: "idle" };` */
export type AppointmentPrintControllerState =
  | { readonly kind: "idle" }
  | { readonly kind: "preparing" }
  | { readonly kind: "presenting" }
  | { readonly kind: "cancelled" }
  | { readonly kind: "system-completed" }
  | { readonly kind: "unknown" }
  | { readonly kind: "expired" }
  | { readonly kind: "failed" };

/** Scheduler print action state and lifecycle commands. @since 1.2.0 @example `const print = useAppointmentPrintController(options);` */
export interface AppointmentPrintController {
  readonly available: boolean;
  readonly active: boolean;
  readonly state: AppointmentPrintControllerState;
  readonly print: () => Promise<void>;
  readonly revoke: () => Promise<void>;
}

/** Own one-at-a-time appointment print attempts without ever re-entering booking. @since 1.2.0 @example `const print = useAppointmentPrintController({ booking, locale: "de-DE", labels });` */
export function useAppointmentPrintController(options: {
  readonly booking: ConfirmedAppointmentPrintBooking | undefined;
  readonly locale: string;
  readonly labels: AppointmentSlipLabels;
  readonly now?: () => number;
}): AppointmentPrintController {
  const platform = usePlatform();
  const capability = platform.appointmentPrinting;
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState<AppointmentPrintControllerState>({ kind: "idle" });
  const activeAttempt = useRef<ActiveAppointmentPrintAttempt | undefined>(undefined);
  const mounted = useRef(true);
  const now = options.now ?? Date.now;

  useEffect(function detectAppointmentPrintingCapability() {
    mounted.current = true;
    if (capability === undefined) {
      setAvailable(false);
      return function markPrintControllerUnmounted() { mounted.current = false; };
    }
    void capability.getCapabilities().then(
      (result) => {
        if (mounted.current) setAvailable(result.available && result.platform === "ipad");
        return undefined;
      },
      () => {
        if (mounted.current) setAvailable(false);
        return undefined;
      },
    );
    return function revokePrintControllerOnUnmount() {
      mounted.current = false;
      const attempt = activeAttempt.current;
      activeAttempt.current = undefined;
      if (attempt !== undefined) {
        clearTimeout(attempt.deadlineTimer);
        void capability.dismiss(attempt.id);
      }
    };
  }, [capability]);

  const revoke = useCallback(async () => {
    const attempt = activeAttempt.current;
    activeAttempt.current = undefined;
    if (attempt !== undefined) {
      clearTimeout(attempt.deadlineTimer);
      if (capability !== undefined) await capability.dismiss(attempt.id);
    }
    if (mounted.current) setState({ kind: "idle" });
  }, [capability]);

  const print = useCallback(async () => {
    if (!available || capability === undefined || options.booking === undefined || activeAttempt.current !== undefined) return;
    const id = crypto.randomUUID();
    const deadlineAt = now() + PRINT_DEADLINE_MS;
    const deadlineTimer = setTimeout(() => {
      if (activeAttempt.current?.id !== id) return;
      activeAttempt.current = undefined;
      void capability.dismiss(id);
      if (mounted.current) setState({ kind: "expired" });
    }, PRINT_DEADLINE_MS);
    const attempt: ActiveAppointmentPrintAttempt = { id, deadlineAt, deadlineTimer };
    activeAttempt.current = attempt;
    setState({ kind: "preparing" });
    const startedAt = performance.now();
    try {
      const [metadata, settings] = await Promise.all([
        docCirrusRpcClient.frontdesk.getAppointmentPrintLocation({
          scheduleId: options.booking.scheduleId,
          expectedPatientId: options.booking.patientId,
        }).catch((): AppointmentPrintLocationResult => ({ outcome: "unavailable", reason: "unavailable" })),
        platform.getPracticeSettings(),
      ]);
      if (activeAttempt.current?.id !== attempt.id) return;
      if (now() >= attempt.deadlineAt) {
        activeAttempt.current = undefined;
        clearTimeout(attempt.deadlineTimer);
        setState({ kind: "expired" });
        return;
      }
      const snapshotNow = now();
      const snapshot = buildAppointmentPrintSnapshot({
        attemptId: attempt.id,
        deadlineAt: attempt.deadlineAt,
        now: snapshotNow,
        booking: options.booking,
        practiceDisplayName: settings.practiceName || "Praxis",
        location: metadata,
        locale: options.locale,
        labels: options.labels,
      });
      setState({ kind: "presenting" });
      const outcome: AppointmentPrintOutcome = await capability.present(snapshot);
      if (activeAttempt.current?.id !== attempt.id) return;
      activeAttempt.current = undefined;
      clearTimeout(attempt.deadlineTimer);
      if (now() >= attempt.deadlineAt) {
        setState({ kind: "expired" });
        return;
      }
      setState({ kind: outcome.outcome });
      if (outcome.outcome === "failed") {
        void reportOperationalError({
          action: "scheduler.appointment-print",
          severity: "error",
          source: "native",
          error: new Error("Appointment print interaction failed"),
          durationMs: Math.round(performance.now() - startedAt),
          context: {
            phase: "presentation",
            platform: "ipad",
            category: outcome.failureCategory ?? "unknown",
          },
        });
      }
    } catch {
      if (activeAttempt.current?.id !== attempt.id) return;
      activeAttempt.current = undefined;
      clearTimeout(attempt.deadlineTimer);
      if (now() >= attempt.deadlineAt) {
        setState({ kind: "expired" });
        return;
      }
      setState({ kind: "failed" });
      void reportOperationalError({
        action: "scheduler.appointment-print",
        severity: "error",
        source: "native",
        error: new Error("Appointment print interaction failed"),
        durationMs: Math.round(performance.now() - startedAt),
        context: { phase: "preparation-or-presentation", platform: "ipad" },
      });
    }
  }, [available, capability, now, options.booking, options.labels, options.locale, platform]);

  return {
    available,
    active: state.kind === "preparing" || state.kind === "presenting",
    state,
    print,
    revoke,
  };
}
